import React from 'react';
import { cn } from '@/lib/utils';

// Custom Brick SVG Icon
const BrickIcon = ({ filled, className }) => (
  <svg
    viewBox="0 0 24 24"
    className={cn("h-5 w-5", className)}
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="1.5"
  >
    {/* Brick shape */}
    <rect x="2" y="6" width="20" height="12" rx="1" />
    {/* Brick lines */}
    <line x1="12" y1="6" x2="12" y2="12" strokeWidth="1.5" />
    <line x1="2" y1="12" x2="22" y2="12" strokeWidth="1.5" />
    <line x1="7" y1="12" x2="7" y2="18" strokeWidth="1.5" />
    <line x1="17" y1="12" x2="17" y2="18" strokeWidth="1.5" />
  </svg>
);

// Display-only brick rating (for showing ratings)
export function BrickRatingDisplay({ rating, maxRating = 5, size = "default", showValue = true }) {
  const sizeClasses = {
    sm: "h-4 w-4",
    default: "h-5 w-5",
    lg: "h-6 w-6",
    xl: "h-8 w-8"
  };

  return (
    <div className="flex items-center gap-1" data-testid="brick-rating-display">
      <div className="flex gap-0.5">
        {[...Array(maxRating)].map((_, index) => (
          <BrickIcon
            key={index}
            filled={index < Math.round(rating)}
            className={cn(
              sizeClasses[size],
              index < Math.round(rating) 
                ? "text-orange-500" 
                : "text-slate-300"
            )}
          />
        ))}
      </div>
      {showValue && (
        <span className="ml-1 font-semibold text-slate-700">
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
}

// Interactive brick rating (for submitting reviews)
export function BrickRatingInput({ 
  value = 0, 
  onChange, 
  maxRating = 5, 
  size = "lg",
  disabled = false 
}) {
  const [hoverValue, setHoverValue] = React.useState(0);

  const sizeClasses = {
    sm: "h-6 w-6",
    default: "h-8 w-8",
    lg: "h-10 w-10",
    xl: "h-12 w-12"
  };

  const handleClick = (rating) => {
    if (!disabled && onChange) {
      onChange(rating);
    }
  };

  return (
    <div 
      className="flex gap-1" 
      data-testid="brick-rating-input"
      onMouseLeave={() => setHoverValue(0)}
    >
      {[...Array(maxRating)].map((_, index) => {
        const ratingValue = index + 1;
        const isFilled = ratingValue <= (hoverValue || value);
        
        return (
          <button
            key={index}
            type="button"
            data-testid={`brick-${ratingValue}`}
            onClick={() => handleClick(ratingValue)}
            onMouseEnter={() => !disabled && setHoverValue(ratingValue)}
            disabled={disabled}
            className={cn(
              "transition-all duration-150 transform",
              !disabled && "hover:scale-110 cursor-pointer",
              disabled && "cursor-not-allowed opacity-50"
            )}
          >
            <BrickIcon
              filled={isFilled}
              className={cn(
                sizeClasses[size],
                isFilled 
                  ? "text-orange-500" 
                  : "text-slate-300 hover:text-orange-300"
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

// Compact version for lists
export function BrickRatingCompact({ rating, totalJobs }) {
  return (
    <div className="flex items-center gap-2" data-testid="brick-rating-compact">
      <BrickIcon filled className="h-4 w-4 text-orange-500" />
      <span className="font-semibold">{rating.toFixed(1)}</span>
      {totalJobs !== undefined && (
        <span className="text-slate-500 text-sm">({totalJobs} trabajos)</span>
      )}
    </div>
  );
}

export default BrickRatingDisplay;
